import React, { useState } from "react";
import { cloneDeep, get } from "lodash";
import {
  BUSINESS_DAYS_NAME_MAP,
  COMPONENT_MODES,
  CTA_SIZE,
  CTA_TYPE,
  DATE_TIME_FORMATS,
} from "../../../data/constants";
import {
  convertArrayToObject,
  isAddNewMode,
  isViewMode,
} from "../../../helpers/catalog-helper";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import { allowEverything, removeSpecialCharAndNumbers } from "../../../utils";
import CtaComponent from "../../../components/reusable-ui/cta";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { DatePicker } from "antd";
import moment from "moment";
import TimelineManagementModal from "./timeline-management";
import ConfirmationModal from "../../../components/reusable-ui/confirmation-modal";
import { convertMomentToString } from "../../../helpers/moment-helper";

interface Props {
  data?: any;
  modeConfig?: any;
  setModeConfig?: any;
  updateItemScheduleDataInParentState?: any;
  errorMessageMap?: any;
  itemSchedulesDataMap?: any;
  itemScheduleId?: string;
  isChangesMade?: boolean;
  onDiscardCtaClick?: any;
  onSaveCtaClick?: any;
}

const ItemScheduleDetailsForm = (props: Props) => {
  const {
    data,
    itemScheduleId = "",
    isChangesMade,
    itemSchedulesDataMap,
    modeConfig,
    setModeConfig,
    updateItemScheduleDataInParentState,
    errorMessageMap,
    onDiscardCtaClick,
    onSaveCtaClick,
  } = props;
  const allChannelsDefDataMap = useSelector(
    (state: RootState) => state.businessReducer?.allChannelsDefDataMap
  );
  const businessBranchesDataMap = useSelector(
    (state: RootState) => state.businessReducer?.businessBranchesDataMap
  );
  const [openTimelineManageModal, setOpenTimelineManageModal] = useState(false);
  const [selectedBusinessDay, setSelectedBusinessDay] = useState("");
  const [isTimelineEditMode, setIsTimelineEditMode] = useState(false);
  const [curDayTimeSlots, setCurDayTimeSlots] = useState<any>([]);
  const [openDeleteTimelineAlert, setOpenDeleteTimelineAlert] = useState(false);

  /**
   * This function will render the customTextField input with the help of textFieldConfig
   * @param textFieldConfig
   */
  function renderTextField(textFieldConfig: any): JSX.Element {
    return (
      <CustomTextField
        textBoxLabel={textFieldConfig.fieldLabel}
        placeholder={textFieldConfig.placeHolder}
        regexCheck={textFieldConfig.regexCheck || removeSpecialCharAndNumbers}
        handleChange={textFieldConfig.onSubmit}
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal
          ${textFieldConfig.customClass || ""}`}
        parentClass={`${textFieldConfig.boxWidth || ""} ${
          textFieldConfig.parentClass || ""
        }`}
        value={textFieldConfig.value}
        rows={textFieldConfig.rows}
        isMandatory={!!textFieldConfig.isMandatory}
        errorMessage={errorMessageMap[textFieldConfig.key] || ""}
        iconClass={textFieldConfig.iconClass || ""}
        isDisabled={isViewMode(modeConfig?.type)}
        maxLength={textFieldConfig?.maxLength}
      ></CustomTextField>
    );
  }

  function renderCustomDropDown(dropdownConfig: any): JSX.Element {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.textBoxLabel}
        optionsMap={dropdownConfig.optionsList}
        isMultiSelect={dropdownConfig.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={(data: any) => {
          if (dropdownConfig.getListItemText)
            return dropdownConfig.getListItemText(data);
          else return data?.name || "";
        }}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={dropdownConfig.onSubmit}
        preSelectedValues={dropdownConfig.preSelectedValues}
        uniqueKey={dropdownConfig.uniqueKey || "mid"}
        isShowLoadMore={dropdownConfig.isShowLoadMore}
        onLoadMoreClick={dropdownConfig.onLoadMoreClick}
        onSearchApi={dropdownConfig.onSearchInput}
        isMandatory={!!dropdownConfig.isMandatory}
        errorMessage={errorMessageMap[dropdownConfig.key] || ""}
        isDisabled={isViewMode(modeConfig?.type)}
        enableSelectAll={!!dropdownConfig.enableSelectAll}
      ></CustomDropdown>
    );
  }

  function handleInputChange(value: any, fieldToUpdate: string) {
    updateItemScheduleDataInParentState(fieldToUpdate, value);
  }

  const tConvert = ({ hh, mm }: any, isEndTime: boolean = false) => {
    let hrsNum = Number(hh),
      minsNum = Number(mm);
    if (isEndTime) {
      minsNum = minsNum + 1;
      if (minsNum >= 60) {
        minsNum -= 60;
        hrsNum += 1;
      }
      if (hrsNum >= 24) {
        hrsNum -= 24;
      }
    }

    let suf = "AM";
    if (hrsNum >= 12) {
      suf = "PM";
    }

    if (hrsNum > 12) {
      hrsNum -= 12;
    }

    if (hrsNum === 0) {
      hrsNum = 12;
    }
    return `${hrsNum.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
    })}:${minsNum.toLocaleString("en-US", { minimumIntegerDigits: 2 })} ${suf}`;
  };

  const onTimelineEditIconClick = (day: string, slots: any) => {
    setOpenTimelineManageModal(true);
    setSelectedBusinessDay(day);
    setIsTimelineEditMode(true);
    setCurDayTimeSlots(slots);
  };

  const onAddNewTimelineClick = () => {
    setOpenTimelineManageModal(true);
    setSelectedBusinessDay("");
    setIsTimelineEditMode(false);
    setCurDayTimeSlots([]);
  };

  const onTimelineModalClose = () => {
    setOpenTimelineManageModal(false);
    setSelectedBusinessDay("");
    setIsTimelineEditMode(false);
  };

  const onTimelineDeleteIconClick = (day: string) => {
    setOpenDeleteTimelineAlert(true);
    setSelectedBusinessDay(day);
  };

  const onDeleteAlertClose = () => {
    setOpenDeleteTimelineAlert(false);
    setSelectedBusinessDay("");
  };

  const deleteTimeline = (day: string) => {
    let businessDaysList: any =
      cloneDeep(get(data, "itemHours.businessDays")) || [];
    let dataToUpdate = businessDaysList.filter(
      (businessDay: any, index: number) => day !== businessDay?.day
    );
    handleInputChange(dataToUpdate, "itemHours.businessDays");
    setOpenDeleteTimelineAlert(false);
    setSelectedBusinessDay("");
  };

  const onTimelineModalSaveCtaClick = (timeSlots: any, selectedDays: any) => {
    let businessDaysList = cloneDeep(get(data, "itemHours.businessDays")) || [];
    let businessDaysMap = convertArrayToObject(businessDaysList, "day");

    let dataToUpdate = [];
    for (let businessDay of Object.keys(BUSINESS_DAYS_NAME_MAP)) {
      if (!selectedDays[businessDay] && !businessDaysMap[businessDay]) {
        continue;
      }

      if (!selectedDays[businessDay]) {
        dataToUpdate.push(businessDaysMap[businessDay]);
      } else {
        let businessDayData: any = {
          slots: timeSlots,
          day: BUSINESS_DAYS_NAME_MAP[businessDay].shortName,
        };
        dataToUpdate.push(businessDayData);
      }
    }

    handleInputChange(dataToUpdate, "itemHours.businessDays");
    setOpenTimelineManageModal(false);
    setSelectedBusinessDay("");
    setIsTimelineEditMode(false);
    setCurDayTimeSlots([]);
  };

  return (
    <section
      className={`${
        isAddNewMode(modeConfig.type) ? "w-full" : "w-[50%] p-[24px] pt-0"
      }`}
    >
      {!isAddNewMode(modeConfig.type) && (
        <div className="flex items-center justify-between">
          <div className="text-[20px] font-normal py-[24px] capitalize">
            {itemSchedulesDataMap[itemScheduleId]?.name}
          </div>
          {isViewMode(modeConfig?.type) && (
            <div
              className="flex cursor-pointer"
              onClick={() =>
                setModeConfig({
                  ...modeConfig,
                  type: COMPONENT_MODES.editMode,
                })
              }
            >
              <div className="rista-sprite ic-pencil-blue"></div>
              <p className="text-14 text-blue font-semibold ml-4">Edit</p>
            </div>
          )}
        </div>
      )}
      <div
        className={`bg-white p-[24px] ${
          !isAddNewMode(modeConfig.type)
            ? "h-[calc(100vh-243px)] overflow-y-auto pb-[66px]"
            : ""
        }`}
      >
        {renderTextField({
          placeHolder: "Enter Name",
          fieldLabel: "Name",
          value: get(data, "name") || "",
          boxWidth: "w-full",
          isMandatory: true,
          key: "name",
          regexCheck: allowEverything,
          parentKey: "",
          onSubmit: (value: any) => handleInputChange(value, "name"),
          parentClass: "mb-[6px]",
          maxLength: 25,
        })}

        {renderTextField({
          placeHolder: "Enter Display Name",
          fieldLabel: "Display Name",
          value: get(data, "displayName") || "",
          boxWidth: "w-full",
          key: "displayName",
          regexCheck: allowEverything,
          parentKey: "",
          onSubmit: (value: any) => handleInputChange(value, "displayName"),
          parentClass: "mb-[6px]",
          maxLength: 25,
        })}

        {renderTextField({
          placeHolder: "Enter Description",
          fieldLabel: "Description",
          value: get(data, "description") || "",
          boxWidth: "w-full",
          rows: 3,
          key: "description",
          regexCheck: allowEverything,
          parentKey: "",
          onSubmit: (value: any) => handleInputChange(value, "description"),
          parentClass: "mb-[6px]",
          maxLength: 50,
        })}

        <div className="flex items-center justify-between mb-[6px]">
          {renderCustomDropDown({
            textBoxLabel: "Channels",
            optionsList: allChannelsDefDataMap || {},
            placeholder: "Select Channel",
            getListItemText: (data: any) => data?.name || "",
            dropdownHeading: "Channels",
            searchPlaceholder: "Search Channel",
            parentClass: "w-[calc(50%-12px)] mr-[24px]",
            isMultiSelect: true,
            enableSelectAll: false,
            isMandatory: true,
            preSelectedValues: get(data, "channelDefIds") || [],
            onSubmit: (value: any) => handleInputChange(value, "channelDefIds"),
            key: "channelDefIds",
            uniqueKey: "channelDefId",
          })}

          {renderCustomDropDown({
            textBoxLabel: "Branches",
            optionsList: businessBranchesDataMap || {},
            placeholder: "Select Branches",
            getListItemText: (data: any) => data?.branchName || "",
            dropdownHeading: "Branches",
            searchPlaceholder: "Search Branch",
            parentClass: "w-[calc(50%-12px)]",
            isMultiSelect: true,
            enableSelectAll: true,
            preSelectedValues: get(data, "branchIds") || [],
            onSubmit: (value: any) => handleInputChange(value, "branchIds"),
            key: "branchIds",
            uniqueKey: "branchId",
          })}
        </div>

        <div className="flex items-center mb-[40px]">
          <div className="w-[calc(50%-12px)] mr-[24px]">
            <p className="text-[14px] text-[#656565] mb-[8px]">Start Date</p>
            <DatePicker
              className="w-full min-h-[46px] rounded-[8px] border-[#EBEBEB]"
              format={"DD/MM/YYYY"}
              value={
                get(data, "startDate")
                  ? moment(get(data, "startDate"), "YYYY-MM-DD")
                  : null
              }
              onChange={(val) =>
                handleInputChange(
                  convertMomentToString(val, DATE_TIME_FORMATS.yearMonthDay),
                  "startDate"
                )
              }
              disabled={isViewMode(modeConfig?.type)}
              // disabledDate={disabledDate}
            />
          </div>

          <div className="w-[calc(50%-12px)]">
            <p className="text-[14px] text-[#656565] mb-[8px]">End Date</p>
            <DatePicker
              className="w-full min-h-[46px] rounded-[8px] border-[#EBEBEB]"
              format={"DD/MM/YYYY"}
              value={
                get(data, "endDate")
                  ? moment(get(data, "endDate"), "YYYY-MM-DD")
                  : null
              }
              onChange={(val) =>
                handleInputChange(
                  convertMomentToString(val, DATE_TIME_FORMATS.yearMonthDay),
                  "endDate"
                )
              }
              disabled={isViewMode(modeConfig?.type)}
              // disabledDate={disabledDate}
            />
          </div>
        </div>

        <div className="">
          <div
            className={`${
              get(data, "itemHours.businessDays")?.length
                ? "mb-[24px] flex items-center justify-between"
                : "mb-[16px]"
            }`}
          >
            <h1 className={`text-[16px] font-medium leading-none`}>Timeline</h1>
            {get(data, "itemHours.businessDays")?.length &&
            !isViewMode(modeConfig?.type) ? (
              <button
                disabled={isViewMode(modeConfig?.type)}
                className="text-[14px] text-[#0074FB] font-semibold cursor-pointer border-0 bg-white"
                onClick={onAddNewTimelineClick}
              >
                + Add More
              </button>
            ) : null}
            {errorMessageMap?.timelines &&
            !get(data, "itemHours.businessDays")?.length ? (
              <p className="text-[12px] text-[#9A3131] font-normal">
                {errorMessageMap?.timelines}
              </p>
            ) : null}
          </div>

          {!get(data, "itemHours.businessDays")?.length ? (
            <button
              disabled={isViewMode(modeConfig?.type)}
              className="text-center w-full text-[16px] py-[9px] bg-[#F1F7FE] border border-[#ADD3FF] rounded-[8px] font-semibold text-[#0074FB] cursor-pointer"
              onClick={onAddNewTimelineClick}
            >
              + Add Timeline
            </button>
          ) : null}

          {get(data, "itemHours.businessDays")?.length
            ? get(data, "itemHours.businessDays")?.map(
                (businessDay: any, index: any) => {
                  return businessDay?.slots?.length ? (
                    <div
                      className="border border-[#EBEBEB] rounded-[8px] mb-[12px] last:mb-0"
                      key={`businessDay-item-${index}`}
                    >
                      <div className="flex items-center justify-between px-[16px] py-[10px] rounded-t-[8px] border-b border-[#EBEBEB] bg-[#F1F7FE]">
                        <p className="text-[16px] font-semibold">
                          {
                            BUSINESS_DAYS_NAME_MAP[
                              businessDay?.day?.toLowerCase()
                            ]?.longName
                          }
                        </p>
                        {!isViewMode(modeConfig?.type) && (
                          <div className="flex items-center">
                            <div
                              className="rista-sprite ic-pencil-grey cursor-pointer mr-[18px]"
                              onClick={() =>
                                onTimelineEditIconClick(
                                  businessDay?.day,
                                  businessDay?.slots
                                )
                              }
                            ></div>
                            <div
                              className="rista-sprite ic-delete-grey cursor-pointer"
                              onClick={() =>
                                onTimelineDeleteIconClick(businessDay?.day)
                              }
                            ></div>
                          </div>
                        )}
                      </div>
                      <div className="px-[16px] py-[14px]">
                        {businessDay?.slots?.length
                          ? businessDay?.slots.map(
                              (slot: any, index: number) => {
                                return (
                                  <span
                                    key={`timelines-${index}`}
                                    className="min-w-fit text-[16px] font-normal leading-none"
                                  >{`${index > 0 ? ", " : ""}${tConvert(
                                    slot?.startTime
                                  )} - ${tConvert(slot?.endTime, true)}`}</span>
                                );
                              }
                            )
                          : null}
                      </div>
                    </div>
                  ) : null;
                }
              )
            : null}
        </div>
      </div>
      {modeConfig.type === COMPONENT_MODES.editMode && (
        <div className="fixed bottom-[0px] z-[111] bg-white h-[70px] px-24 flex justify-center items-center overflow-hidden right-0 w-[calc(50%-48px)] border-t-[1px] border-[#f5f5f5]">
          <CtaComponent
            ctaText={"Discard"}
            ctaType={CTA_TYPE.secondary_cta}
            ctaSize={CTA_SIZE.medium}
            onCtaClick={onDiscardCtaClick}
            customClass={"w-[calc(50%-10px)] mr-10"}
          ></CtaComponent>
          <CtaComponent
            ctaText={"Save Changes"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={CTA_SIZE.medium}
            onCtaClick={onSaveCtaClick}
            customClass={"w-[calc(50%-10px)] ml-10"}
            isDisabled={!isChangesMade}
          ></CtaComponent>
        </div>
      )}

      {openTimelineManageModal && (
        <TimelineManagementModal
          daySelected={selectedBusinessDay}
          isTimelineEditMode={isTimelineEditMode}
          onModalClose={onTimelineModalClose}
          curDayTimeSlots={curDayTimeSlots}
          onSaveCtaClick={onTimelineModalSaveCtaClick}
          tConvert={tConvert}
        />
      )}

      {openDeleteTimelineAlert && (
        <ConfirmationModal
          onModalClose={onDeleteAlertClose}
          onConfirmBtnClick={() => deleteTimeline(selectedBusinessDay)}
          headingText="Remove Timeline?"
          description="This is an irreversible action. Do you still want to continue?"
        />
      )}
    </section>
  );
};

export default ItemScheduleDetailsForm;
